import { useUser } from '@/app/providers';
import { PageContentCard } from '@/components/layout/PageContentCard';
import RevoCareProLogo from '@/components/assets/RevoCareProLogo';
import { FileText } from '@/design-system/icons';
import { hasFeature } from '@/domain/authentication';
import type { ServiceContractSummary } from '@/features/devices/types';
import { useSidePanelHandler } from '@/hooks/useSidePanelHandler';
import { formatDate } from '@/utils/dateUtils';
import { FilePlus } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { getLatestContractDocumentUrl } from '../api';
import { useDeviceContracts } from '../hooks';
import {
  isCurrentContract,
  type ContractStatus,
  type ServiceContract,
} from '../types';
import { ContractStatusBadge } from './ContractStatusBadge';
import { ServiceContractSidePanel } from './ServiceContractSidePanel';

interface ServiceContractCardProps {
  deviceId: string;
  company: { id: string; name: string };
  serviceContract: ServiceContractSummary | null;
}

type AdminContractPanelContent =
  | { type: 'create' }
  | { type: 'manage'; contract: ServiceContract };

// ServiceContractCard shows the device's service contract state. Without the
// servicecontract.admin feature it is read-only (sourced from the device details summary);
// with it, the full contract history and lifecycle management become available.
export function ServiceContractCard({
  deviceId,
  company,
  serviceContract,
}: ServiceContractCardProps) {
  const { user } = useUser();
  const isAdmin = Boolean(user && hasFeature(user, 'servicecontract.admin'));

  if (isAdmin) {
    return <AdminContractCard deviceId={deviceId} companyId={company.id} />;
  }
  return (
    <ReadOnlyContractCard
      deviceId={deviceId}
      serviceContract={serviceContract}
    />
  );
}

// ReadOnlyContractCard renders status, validity dates and a link to the latest document,
// relying solely on the summary embedded in the device details response.
function ReadOnlyContractCard({
  deviceId,
  serviceContract,
}: {
  deviceId: string;
  serviceContract: ServiceContractSummary | null;
}) {
  const { t } = useTranslation();

  const handleOpenLatest = async () => {
    try {
      const url = await getLatestContractDocumentUrl(deviceId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error(t('serviceContracts.documents.openError'));
    }
  };

  return (
    <PageContentCard className="w-full">
      <CardHeader />
      {serviceContract ? (
        <div className="flex flex-col gap-1">
          <div>
            <ContractStatusBadge
              status={serviceContract.status as ContractStatus}
            />
          </div>
          <div className='flex-row'>
            <span className='text-muted text-sm me-1'>
              {t('serviceContracts.fields.servicePeriod')}:
            </span>
            <ContractValidity
              signedOn={serviceContract.signedOn}
              expiresOn={serviceContract.expiresOn}
            />
          </div>
          {serviceContract.document && (
            <button
              type="button"
              onClick={handleOpenLatest}
              className="text-primary inline-flex items-center gap-2 text-sm hover:underline"
            >
              <FileText className="size-4" />
              {t('serviceContracts.documents.openLatest')}
            </button>
          )}
        </div>
      ) : (
        <p className="text-muted text-sm">{t('serviceContracts.noContract')}</p>
      )}
    </PageContentCard>
  );
}

// AdminContractCard lists every contract as a selectable card and keeps creation available
// when no Requested, Active or Locked contract exists.
function AdminContractCard({
  deviceId,
  companyId,
}: {
  deviceId: string;
  companyId: string;
}) {
  const { data: contracts, isLoading } = useDeviceContracts(deviceId);
  const sidePanel = useSidePanelHandler<AdminContractPanelContent>();

  // Show newest contracts first so the active/most recent one sits at the top.
  const sortedContracts = useMemo(
    () =>
      [...(contracts ?? [])].sort((a, b) =>
        (b.createdAt ?? b.signedOn ?? '').localeCompare(
          a.createdAt ?? a.signedOn ?? ''
        )
      ),
    [contracts]
  );
  const canCreateContract = useMemo(
    () => !(contracts ?? []).some(isCurrentContract),
    [contracts]
  );
  const selectedContractId =
    sidePanel.content?.type === 'manage' ? sidePanel.content.contract.id : null;
  // Resolve the contract from the live query cache so the panel reflects the
  // latest status after a successful lifecycle mutation.
  const selectedContract =
    contracts?.find((c) => c.id === selectedContractId) ?? null;

  return (
    <PageContentCard className="w-full">
      <CardHeader />
      <AdminContractList
        isLoading={isLoading}
        contracts={sortedContracts}
        canCreateContract={canCreateContract}
        onCreateContract={() => sidePanel.open({ type: 'create' })}
        onSelectContract={(contract) =>
          sidePanel.open({ type: 'manage', contract })
        }
      />

      <ServiceContractSidePanel
        deviceId={deviceId}
        companyId={companyId}
        contract={selectedContract}
        isOpen={sidePanel.isOpen}
        panelKey={sidePanel.panelKey}
        onClose={sidePanel.close}
      />
    </PageContentCard>
  );
}

function AdminContractList({
  isLoading,
  contracts,
  canCreateContract,
  onCreateContract,
  onSelectContract,
}: {
  isLoading: boolean;
  contracts: ServiceContract[];
  canCreateContract: boolean;
  onCreateContract: () => void;
  onSelectContract: (contract: ServiceContract) => void;
}) {
  const { t } = useTranslation();

  if (isLoading) {
    return <p className="text-muted text-sm">{t('common.loading')}</p>;
  }

  return (
    <ul className="space-y-2">
      {canCreateContract && <CreateContractCard onSelect={onCreateContract} />}
      {contracts.map((contract) => (
        <SelectableContractCard
          key={contract.id}
          contract={contract}
          onSelect={() => onSelectContract(contract)}
        />
      ))}
    </ul>
  );
}

// CreateContractCard exposes contract creation as the same selectable card pattern.
function CreateContractCard({ onSelect }: { onSelect: () => void }) {
  const { t } = useTranslation();

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className="border-border hover:border-primary/50 hover:bg-surface-darker focus:ring-primary flex w-full items-center justify-between gap-3 rounded-lg border border-dashed px-3 py-3 text-left transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none"
      >
        <span className="text-onsurface inline-flex items-center gap-2 text-sm font-medium">
          <FilePlus className="text-primary size-4" />
          {t('serviceContracts.actions.create')}
        </span>
        <span className="text-muted text-sm">
          {t('serviceContracts.noContract')}
        </span>
      </button>
    </li>
  );
}

// SelectableContractCard opens the sidepanel for the exact contract the admin chose.
function SelectableContractCard({
  contract,
  onSelect,
}: {
  contract: ServiceContract;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className="border-border hover:border-primary/50 hover:bg-surface-darker focus:ring-primary flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-3 text-left transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none"
      >
        <ContractStatusBadge status={contract.status} />
        <ContractValidity
          signedOn={contract.signedOn}
          expiresOn={contract.expiresOn}
        />
      </button>
    </li>
  );
}
/* 
function ContractStatusLabel({
  status,
  size,
  className,
}: {
  status: ContractStatus;
  size?: 'sm' | 'md';
  className?: string;
}) {
  console.log(status)
  return (
    <StatusLabel
      status={status}
      translationPrefix="serviceContracts.status"
      styles={contractStatusLabelStyles}
      size={size}
      className={className}
    />
  );
}
 */
// ContractValidity renders the "signed → expires" date range shared by both views.
function ContractValidity({
  signedOn,
  expiresOn,
}: {
  signedOn: string | null;
  expiresOn: string | null;
}) {
  const { t } = useTranslation();
  return (
    <span className="text-onsurface text-sm font-semibold">
      {t('serviceContracts.validity', {
        from: formatDate(signedOn ?? undefined),
        until: formatDate(expiresOn ?? undefined),
      })}
    </span>
  );
}

// CardHeader renders the REVOCare Pro brand logo as the section title.
function CardHeader() {
  return <RevoCareProLogo className="h-6 w-auto mb-4" />;
}
